import { useMemo, useState } from 'react'
import { CheckCircleOutlined, IconCheck, SearchOutlined } from '../../../components/icons'
import { Button } from '../../../components/ui/Button'
import { formatPrice } from '../../parts/format'
import {
  MATCH_QUALITY_LABEL,
  type MatchQuality,
  type MatchedPart,
  type SourcingOption,
} from '../staging-types'
import { PartThumbnail } from './PartThumbnail'

/**
 * THE KEEP ROW'S COPY, ONE COPY OF IT, FOR BOTH ARMS.
 *
 * ==== HUMAN RULING, 2026-09-09 — REVERSES ADR-0146 Am.1 D12 ================
 * The catalog arm previously carried bespoke, catalog-accurate copy ("Keep this
 * catalog match" / "Use {vendor} {sku} — don't swap") because ADR-0146 **D4**
 * forbade upload-asserting wording for a population that has no uploaded file,
 * and **Am.1 D12** closed OQ-C by ruling that bespoke copy in.
 *
 * A human product decision on 2026-09-09 REVERSED that: ship the design's
 * literal string (`Fictiv Platform.dc.html:3243`) on BOTH arms. The tradeoff
 * was put explicitly (that this asserts an upload which does not exist for a
 * BOM-stated catalog part number) and accepted. This comment exists so the
 * next reader finds the REASON rather than concluding D4 was simply missed —
 * D4 was understood, surfaced, and overruled by the person entitled to.
 *
 * FOLLOW-UP OWED (frontend-engineer → PM/product-designer): ADR-0146 D4 and
 * Am.1 D12 are now stale in the record and need a written amendment. Code
 * comments are not the decision record; this is a pointer to a debt, not a
 * substitute for paying it.
 *
 * WHY CONSTANTS AND NOT TWO LITERALS: the two arms' copy is now identical, and
 * two identical literals 20 lines apart is exactly how they drifted the first
 * time. The arms still differ in WRITE PATH (`onKeepUploaded` is `partId`-
 * scoped, `onKeepBomReference` is node-scoped — ADR-0162 D5, untouched by this
 * ruling, which was about copy only), so the branches stay separate; only the
 * strings are shared. `KEEP_ROW_SUB_LABEL` is a function because the design
 * interpolates the part name.
 */
const KEEP_ROW_TITLE = 'Keep my uploaded part'
const KEEP_ROW_SUB_LABEL = (partName: string): string => `Use “${partName}” as-is — no catalog swap`

/**
 * The keep row. ONE markup definition, both arms.
 *
 * ==== THE "Keep" BUTTON IS GONE (2026-09-09) ==============================
 * There is NO Keep button in the design, and there never was. `dc.html:3241`
 * puts `onClick="{{ r.onKeep }}"` + `cursor:pointer` + `class="fx-row"` on the
 * ROW ITSELF; the only thing drawn on the right is the "Kept" pill, and only
 * once `r.keepSel` is true. The outlined `<Button>Keep</Button>` this component
 * used to render was an invention — and a self-documented one: the CSS rule for
 * it (`.staging-sourcing-keep .btn`) openly said "The design has no Keep BUTTON
 * here at all — its whole row is the affordance", then sized the invented button
 * to match the option chips anyway. Reported by the human on 2026-09-09.
 *
 * WHY THIS IS A `<button>` AND NOT A CLICKABLE `<div>`: the design's row is a
 * bare `div` with an `onClick`, which is a WCAG 2.1.1 (Keyboard) and 4.1.2
 * (Name, Role, Value) failure — not reachable by Tab, no role, no accessible
 * name, invisible to assistive tech. The prototype is a visual reference, not
 * an accessibility model (the standing position recorded in
 * `staging-bom-full-parity-ux-spec.md` §6/row 7 for this very panel). So the
 * row takes the design's LOOK — full-bleed, no chrome, no button-shaped control
 * — while being a real `<button>`: keyboard operable, focusable, and named by
 * its own title + sub-line, which read as one sentence.
 *
 * Contents are phrasing-level `<span>`s, not `<div>`s: only phrasing content is
 * valid inside `<button>`, and `.parts-row-name` is `display:inline-flex`, so
 * the styling is identical either way.
 */
function KeepRow(props: {
  readonly onKeep: () => void
  readonly partName: string
  /**
   * The uploaded part's own thumbnail — the SAME image the BOM line's main
   * thumbnail renders for this part (`StagingBomLeafRow`'s
   * `part.thumbnailUrl` / this panel's own option-row thumbnail below, both
   * fed from `part.thumbnailUrl`). The Keep row IS that part, so it shows
   * that part's real image rather than a generic placeholder — `PartThumbnail`
   * falls back to the shared decorative glyph on its own when this is
   * `undefined`, so a part with no image yet still renders correctly.
   */
  readonly thumbnailUrl: string | null | undefined
  /**
   * The design's `r.keepSel` pill (`dc.html:3245`). Passed in rather than
   * derived here because the two arms genuinely differ: the catalog arm has the
   * design's own `kept` state, whereas the FLAT surface has no keep row in the
   * design at all and has never rendered a pill — so it keeps passing `false`
   * and its behaviour is byte-unchanged by this refactor. Widening it to both
   * arms would be a new, unreviewed state on a surface the design is silent on.
   */
  readonly showKeptPill: boolean
}) {
  return (
    <button
      type="button"
      className="staging-sourcing-option staging-sourcing-keep"
      onClick={props.onKeep}
    >
      {/* Same thumbnail as the BOM line's main icon for this part
          (`part.thumbnailUrl`) — not a generic placeholder. `PartThumbnail`
          still falls back to the shared decorative glyph on its own when the
          part has no image yet. */}
      <PartThumbnail className="staging-row-thumb" url={props.thumbnailUrl} />
      <span className="parts-row-name">
        <span>{KEEP_ROW_TITLE}</span>
        <span className="caption-text">{KEEP_ROW_SUB_LABEL(props.partName)}</span>
      </span>
      {props.showKeptPill && (
        <span className="staging-sourcing-keep-kept">
          {/* `IconCheck` is the design's EXACT glyph — its path is
              `M20 6 9 17l-5-5` (icons.tsx:562), the same one `dc.html:3245`
              draws here. Not `CheckCircleOutlined`, the circled variant the
              design uses elsewhere. Decorative: the word "Kept" is beside it. */}
          <IconCheck size={11} />
          Kept
        </span>
      )}
    </button>
  )
}

export interface MatchedSourcingPanelProps {
  readonly part: MatchedPart
  readonly onSelectOption: (optionId: string) => void
  /**
   * The FILE-backed matched leaf's keep arm (`MatchedPartRow`'s population, and
   * a file-backed leaf in the BOM tree). `partId`-scoped.
   *
   * STILL OPTIONAL, and still mutually exclusive with `onKeepBomReference`
   * below — but NO LONGER because of the copy. This doc comment used to say the
   * row "only makes sense for a FILE-backed matched leaf" since its copy
   * asserts an upload a catalog leaf never has, per ADR-0146 D4. That rationale
   * was REVERSED by human ruling on 2026-09-09 (see `KEEP_ROW_TITLE` at the top
   * of this file): both arms now render the design's literal upload wording, so
   * the copy no longer discriminates between them.
   *
   * What still discriminates is the WRITE PATH: a catalog leaf has no
   * `staged_file` row, so the `partId`-scoped verb this prop carries would 404
   * for it (ADR-0162 D5 routes that population through `onKeepBomReference`
   * instead). Exactly one of the two is ever supplied
   * (`staged_bom_node_resolution_arm_mutex`, migration 1013).
   *
   * Do NOT default this to a no-op function — a rendered-but-inert keep row is
   * the ADR-0145 D7 defect the ADR record exists to prevent.
   */
  readonly onKeepUploaded?: (() => void) | undefined
  /**
   * ADR-0146 **Am.1 D12** — the CATALOG-leaf keep arm, and the reason the row
   * above it is no longer suppressed on this surface.
   *
   * MUTUALLY EXCLUSIVE with `onKeepUploaded` by construction: a leaf is either
   * file-backed or a pure catalog reference (`staged_bom_node_resolution_arm_
   * mutex`, migration 1013), never both, so exactly one of these two is ever
   * supplied.
   *
   * They remain TWO PROPS rather than one handler plus a flag — but the reason
   * changed on 2026-09-09. It used to be that they "carry DIFFERENT COPY, and
   * getting that copy wrong is the whole subject of ADR-0146 D4". The human
   * ruling recorded at `KEEP_ROW_TITLE` unified the copy, so that reason is
   * gone. The surviving reason is the write path: these two verbs address
   * DIFFERENT SUBJECTS (a `partId` vs a `nodeId`) against different tables, and
   * collapsing them to one handler plus a boolean would make it possible to
   * dispatch a part-scoped write for a node-scoped leaf — a 404, not a
   * mis-worded label.
   *
   * Writes the `'kept-bom-reference'` arm, whose server verb, route, table and
   * projection have existed since ADR-0146 (Am.1 D14 verified this before the
   * ruling). ADR-0146 D9 test 2 governs it: this must be proven to WRITE via
   * an independent store re-read, not a spy — a rendered-but-inert keep row is
   * the ADR-0145 D7 defect that record exists to prevent.
   */
  readonly onKeepBomReference?: (() => void) | undefined
  /**
   * ADR-0146 D8 — the disclosure trigger that opens this panel points at it
   * via `aria-controls={id}`. Optional so existing callers that don't yet
   * need the wiring keep compiling; every ADR-0146 caller supplies one.
   */
  readonly id?: string
  /**
   * UID-494 (T7) design-parity gap review — this panel is shared between TWO
   * comp surfaces that are NOT visually identical: `MatchedPartRow`'s legacy
   * flat bucket (`matchedRows`, `Fictiv Platform.dc.html:3356-3378`, WHICH
   * HAS an in-panel search box, and NO left accent bar) and the BOM-tree
   * leaf's own expansion (`bomRows`' `stdRow`, `:9384-9426` / rendered at
   * `:3239-3260`, which has NO search box but DOES have a left accent bar).
   * `default` (the pre-existing behavior) keeps `MatchedPartRow`
   * byte-identical; `'bom-tree'` is the ONLY value `StagingBomLeafRow`
   * passes, and is what gates the two surface-specific differences below.
   */
  readonly variant?: 'default' | 'bom-tree'
}

/**
 * UID-495 (T8) — the in-panel option filter, mirroring the comp's `oq`
 * (`Fictiv Platform.dc.html:9342`):
 * `(o.desc + ' ' + o.vendor + ' ' + o.sku).toLowerCase().includes(oq)`.
 *
 * `optionTitle` stands in for the comp's `desc` and is coalesced to `''`
 * rather than skipped, so an option that predates T4/UID-491 still matches on
 * vendor + SKU instead of dropping out of every non-empty search.
 *
 * Substring (not token/fuzzy) matching, and `includes` over the JOINED string,
 * exactly as the comp does — so a query may straddle the fields in that order.
 * Deliberately NOT normalised beyond case: the comp does no trimming per-field
 * and no diacritic folding, and inventing either here would make the filter
 * disagree with the design on real catalog SKUs.
 */
function matchesQuery(option: SourcingOption, query: string): boolean {
  return `${option.optionTitle ?? ''} ${option.vendor} ${option.sku}`.toLowerCase().includes(query)
}

/**
 * The WORD the design's single match cell shows (`dc.html:9421`'s `o.match` —
 * `Exact`/`Similar`/`Partial`), or `null` when this option carries no quality
 * signal at all and must render the honest-absence `—` (ADR-0146 D8.6).
 *
 * TWO SOURCES, IN PRECEDENCE ORDER, AND THE SECOND IS A RENAME NOT A GUESS:
 *
 *  1. `matchQuality` — the real field (T4/UID-491), used verbatim when present.
 *     The demo/BOM arm always sends it (`demo-sourcing-projection.ts:381`).
 *
 *  2. `matchPercent === 100` → `'exact'`. Legitimate because `matchPercent` is
 *     contractually only ever `100` or `null` (ADR-0146 D2), and D2 defines the
 *     `100` as "a stated identity, not a measurement" — which is the same claim
 *     the word "Exact" makes. No information is added; a boolean that was being
 *     printed as a percentage is printed as the word the design uses. This
 *     fallback is what kept the real catalog arm rendering something true
 *     BEFORE UID-573 T2 wired it up to send `matchQuality` directly; it stays
 *     as the honest-absence path for any producer that still omits the field.
 *
 * DELIBERATELY NOT DERIVED: anything else. A `null`/absent percent does NOT
 * become `'similar'` or `'partial'` — those are graded similarity claims, and
 * manufacturing one from an absence is exactly the fabricated-confidence harm
 * ADR-0146 D2 exists to forbid (and OQ-B still owns). Absence stays absence.
 */
function displayedMatchQuality(option: SourcingOption): MatchQuality | null {
  if (option.matchQuality != null) {
    return option.matchQuality
  }
  return option.matchPercent === 100 ? 'exact' : null
}

/**
 * The expandable sourcing-options panel from `MatchedPartRow`'s "Swap" flow
 * (search-completed header, "Keep my uploaded part" row, per-option
 * Select/Selected table) — extracted so `StagingBomLeafRow` (ADR-0086) can
 * reuse the SAME markup/behavior for a matched leaf's expanded state,
 * instead of reimplementing it. `MatchedPartRow` renders this component in
 * place of its previous inline JSX; the rendered DOM is unchanged
 * (characterization-safe refactor).
 *
 * ADR-0146 D11 step 5 — also reused, unmodified in shape, for the
 * catalog-leaf sourcing dropdown: the same header/table/Select-button
 * markup, minus the Keep row (see `onKeepUploaded`'s doc comment) and fed
 * `matchPercent: null` candidates (D2), which is why the Match %/Price cells
 * below carry an `sr-only` equivalent for their `—` glyph (D8.6) rather than
 * a bare em-dash.
 *
 * ## UID-495 (T8) — design parity
 *
 * Brings the dropdown up to the comp (`dc.html:9419-9422`, the BOM-mode
 * `stdRow` option map — NOT the flat matched-row map at `:3368`, which is a
 * different surface): option title, match-quality badge, lead-time TEXT,
 * teal-tinted selected row, in-panel search, keep-row match count, and the
 * row's thumbnail on each option.
 *
 * THE TWO NEW COLUMNS ARE CONDITIONAL on data presence (`showTitle` /
 * `showQuality`) rather than always emitted. This panel is shared with
 * `MatchedPartRow` (the legacy flat bucket), whose options carry none of the
 * T4/UID-491 fields; always emitting the columns would leave that surface
 * with two permanently empty cells per row — a visible regression on a
 * surface this ticket requires to be unaffected. Gating on
 * `options.some(...)` (not on a prop) keeps the decision driven by the data
 * the caller actually has, so no caller has to opt in and the BOM surface
 * gets full parity automatically.
 */
export function MatchedSourcingPanel({
  part,
  onSelectOption,
  onKeepUploaded,
  onKeepBomReference,
  id,
  variant = 'default',
}: MatchedSourcingPanelProps) {
  const options = part.sourcingOptions
  const [query, setQuery] = useState('')
  const isBomTree = variant === 'bom-tree'

  /* Trimmed + lower-cased ONCE per keystroke rather than inside the filter
   * predicate, matching the comp's own `oq` derivation. A whitespace-only
   * query collapses to `''` and therefore shows everything — typing a space
   * must not empty the table. */
  const normalizedQuery = query.trim().toLowerCase()
  const shownOptions = useMemo(
    () =>
      normalizedQuery === ''
        ? options
        : options.filter((option) => matchesQuery(option, normalizedQuery)),
    [options, normalizedQuery],
  )

  const showTitle = options.some((option) => (option.optionTitle ?? '') !== '')

  /* ==== DEFECT FIX, 2026-09-09 — ONE MATCH COLUMN, A WORD NOT A PERCENTAGE ==
   * Reported by the human: this cell showed a percentage where the design shows
   * `Exact`/`Partial`, and its icon was not coloured.
   *
   * THE PREVIOUS PASS HAD ALREADY DIAGNOSED THIS AND STOPPED SHORT. The comment
   * deleted from here said, verbatim: "`dc.html:9421` binds `o.match` to a WORD
   * (`Exact`/`Similar`/`Partial`), never a number, on BOTH surfaces (`:3253`
   * BOM-expanded, `:3370` flat) — THE DESIGN HAS NO PERCENT COLUMN AT ALL". It
   * then merely GATED the percent column on data presence instead of removing
   * it. Same shape as the invented Keep button: the comment names the real
   * defect and the code then dresses it. Once you have written "the design has
   * no X", the fix is to delete X.
   *
   * So the two columns (`Match %` + `Match quality`) collapse into the design's
   * ONE 56px cell, rendering the word.
   *
   * WHY THIS FABRICATES NOTHING. `matchPercent` is, by contract, only ever
   * `100` or `null` (`staging-types.ts`: "only ever `100` (a stated identity)
   * or `null` (no score exists) per ADR-0146 D2, so it cannot express 'similar'
   * or 'partial' at all"). It is a boolean wearing a percent sign. Mapping
   * `100 → 'exact'` therefore RENAMES a fact rather than deriving a new one:
   * D2 itself calls 100 "a stated identity, not a measurement", which is
   * precisely what "Exact" asserts. Nothing is inferred in the other direction
   * — a candidate with no quality signal renders the honest-absence `—` plus
   * its sr-only equivalent (D8.6), never a guessed "Similar".
   *
   * ADR-0146 D2 / D8.6 / OQ-B are all intact: no score is invented, no absence
   * is filled, and the `matchPercent` FIELD is untouched on the wire.
   *
   * CLOSED (UID-573 T2): the real catalog arm's `catalogItemToSourcingOption`
   * now sends `matchQuality` directly — `'exact'` on the hit, `'similar'` on
   * every non-exact candidate (`catalog-leaf-resolution.ts`) — so this surface
   * shows `Similar` for a real catalog candidate the same way the demo arm
   * already did (`demo-sourcing-projection.ts:381`). No view change was
   * needed: `displayedMatchQuality`'s field-first precedence (above) already
   * preferred `matchQuality` when present. */
  const showMatch = options.some((option) => displayedMatchQuality(option) !== null)

  /* DESIGN-CONFORMITY FIX (UID-494 T7 gap review, finding 4) — `keepMeta`
   * ("N catalog matches found") is REMOVED. It transcribed the comp's
   * `keptMeta` (`dc.html:9413`), which — grep-verified — is a COMPUTED BUT
   * NEVER RENDERED field: no template anywhere in the comp reads
   * `r.keptMeta`. Same "computed but dead" pattern this file already
   * resolved once for `matchChipSub` (`StagingBomLeafRow.tsx`) — the comp
   * does not show this text, full stop.
   */

  /* The design's own `kept`: `dc.html:9385-9392` computes
   * `const kept = needsSelection` where `needsSelection = !chosen` — i.e. the
   * keep row reads "Kept" exactly when NO catalog option is chosen. Derived
   * rather than taken from a new field so it cannot disagree with the option
   * rows it sits above.
   *
   * NOTE for whoever reads this next, because it is counter-intuitive: on the
   * CATALOG surface this is presently unreachable, and correctly so. A catalog
   * leaf exists because a SKU matched exactly, and
   * `catalog-leaf-resolution.ts:623` always ships that exact hit
   * `selected: true` (candidates are never pre-selected). So `isKept` is
   * `false` for every catalog leaf today — which is precisely what the design
   * does too, since its `chosenId = swappedId || (hasExact ? opts[0].id : null)`
   * is likewise never null when there IS an exact hit. The branch is
   * implemented because it is the design's specified state and becomes
   * reachable the moment a population without an exact hit reaches this panel;
   * it is NOT dead code guarded by a fabricated condition. */
  const isKept = !options.some((option) => option.selected)

  /* DESIGN-CONFORMITY FIX (UID-494 T7 gap review, finding 2) — the in-panel
   * search box exists ONLY on the flat `matchedRows` surface's own
   * expansion (`dc.html:3361`'s `m.optSearch`). The BOM-tree surface's OWN
   * expansion (`:3239-3260`, `bomRows`' `r.` fields) has no such control at
   * all — T8/UID-495 built it for both surfaces despite its own doc comment
   * (above) citing the BOM-mode map as the reference. `showSearch` is
   * therefore FALSE unconditionally on the `bom-tree` variant, regardless of
   * option count. */
  const showSearch = !isBomTree && options.length > 1

  return (
    <div
      className={
        isBomTree
          ? 'staging-sourcing-panel staging-sourcing-panel-bom-tree'
          : 'staging-sourcing-panel'
      }
      id={id}
    >
      <div className="staging-sourcing-panel-bar">
        {/* DESIGN-CONFORMITY FIX (UID-494 T7 gap review, finding 1), SCOPED TO
            `bom-tree` ONLY — the BOM-tree surface's header
            (`dc.html:3241`, `r.optionCount`) is plain text, no checkmark, no
            "found". The FLAT surface's own header is a COMPLETELY DIFFERENT
            string (`:3359`'s `m.expandHint`, e.g. "N similar parts found —
            pick one to move forward" / "Direct catalog match — N
            interchangeable options") — NOT "Search completed" at all, and
            fixing THAT divergence is unreviewed, out of this gap review's
            scope (which only checked BOM-tree rows), so the flat/default
            variant's existing text is left exactly as it was. Singular/
            plural ("sourcing option" vs "options") on the bom-tree branch is
            a deliberate, non-regressive grammar fix ON TOP of the comp
            (which hardcodes plural even at N=1) — not itself a flagged
            deviation. */}
        <p className="staging-sourcing-panel-header">
          {isBomTree ? (
            <>
              Search completed · {options.length} sourcing option
              {options.length === 1 ? '' : 's'}
            </>
          ) : (
            <>
              <CheckCircleOutlined aria-hidden="true" /> Search completed · {options.length}{' '}
              sourcing option
              {options.length === 1 ? '' : 's'} found
            </>
          )}
        </p>
        {showSearch && (
          <div className="staging-sourcing-search">
            <SearchOutlined aria-hidden="true" />
            <label className="sr-only" htmlFor={`${id ?? part.partId}-option-search`}>
              Search sourcing options for {part.name}
            </label>
            <input
              id={`${id ?? part.partId}-option-search`}
              type="search"
              className="staging-sourcing-search-input"
              placeholder="Search catalog for another part…"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </div>
        )}
      </div>
      {onKeepUploaded && (
        <KeepRow
          onKeep={onKeepUploaded}
          partName={part.name}
          thumbnailUrl={part.thumbnailUrl}
          showKeptPill={false}
        />
      )}
      {/* The catalog-leaf keep arm. ITS COPY IS NOW THE DESIGN'S LITERAL, SHARED
          WITH THE FILE-BACKED ARM ABOVE — see `KEEP_ROW_TITLE`'s comment at the
          top of this file for the 2026-09-09 human ruling that reversed
          ADR-0146 Am.1 D12 and overruled D4's prohibition, and for the ADR
          amendment still owed.
          What did NOT change: the WRITE PATH. This arm dispatches
          `onKeepBomReference` (node-scoped, ADR-0162 D5), never the
          `partId`-scoped `onKeepUploaded` — the ruling was about wording, and a
          catalog leaf still has no `staged_file` row for a part-scoped verb to
          address. The two branches therefore remain separate despite now
          rendering identical text. */}
      {/* `isKept` is the design's OWN derivation, not a new concept:
          `dc.html:9385-9392` computes `const kept = needsSelection`, and
          `needsSelection = !chosen` — so "kept" IS "no catalog option is
          chosen". Derived from `options` so no contract field is invented and
          the two cannot disagree.
          NOTE the pill no longer REPLACES a control, because there is no longer
          a control to replace: the row itself is the affordance in both states,
          exactly as `dc.html:3241` has it (its `onClick` is unconditional). So
          keeping stays re-invocable from the kept state — an idempotent
          re-keep, which is what the design does too. */}
      {onKeepBomReference && (
        <KeepRow
          onKeep={onKeepBomReference}
          partName={part.name}
          thumbnailUrl={part.thumbnailUrl}
          showKeptPill={isKept}
        />
      )}
      {options.length === 0 ? (
        <p className="staging-sourcing-empty">No sourcing options found for this part.</p>
      ) : shownOptions.length === 0 ? (
        /* The comp's `noOptResults` state and its copy verbatim
         * (`dc.html:3364`). A DISTINCT state, not an empty table: a table with
         * a header and no rows reads to a screen-reader user as a table that
         * failed to load, and gives a sighted user no hint that their own
         * query caused it. `<output>` carries an implicit role="status"
         * (HTML-AAM) — the semantic element biome prefers over a bare
         * `role="status"`, and the same choice `EmptyState`/`ToastProvider`
         * already make — so the state is ANNOUNCED when a query empties the
         * list, not just drawn. */
        <output className="staging-sourcing-no-results">No catalog parts match that search.</output>
      ) : (
        <table className="parts-table staging-sourcing-table">
          {/* Widths live on `<col>`, NOT on the `<th>`/`<td>`, and that is
              load-bearing: `table-layout: fixed` derives column widths from
              the first row's cells, but this table's `<th>`s are taken out of
              flow by the clip-based visually-hidden treatment (see
              `uploads.css`), leaving fixed layout with no cells to measure —
              every column collapses. `<colgroup>` is independent of the header
              cells' positioning, so the two techniques coexist.
              The mirroring of the conditional columns below is deliberate: a
              `<col>` count that disagrees with the `<th>`/`<td>` count silently
              shifts every width by one column. */}
          <colgroup>
            {/* Unsized on purpose — absorbs the remainder, which is the
                design's `flex: 1` on the option title.
                ALWAYS EMITTED as of 2026-09-09 (was `{showTitle && …}`) — see
                the matching `<td>`'s comment: this column carries the option
                THUMBNAIL, which the design draws unconditionally, so it cannot
                be gated on the title text. */}
            <col />
            <col className="staging-sourcing-col-vendor" />
            {showMatch && <col className="staging-sourcing-col-match" />}
            <col className="staging-sourcing-col-price" />
            <col className="staging-sourcing-col-select" />
          </colgroup>
          <thead>
            <tr>
              {/* The per-column classes carry the design's column widths.
                  They replace an `nth-child(3)/(4)` rule, which was fragile by
                  construction: `showTitle`/`showPercent`/`showQuality` each
                  shift every later column's index, so a positional selector
                  silently lands on a different column per data shape. */}
              {/* ALWAYS EMITTED (was `{showTitle && …}`) — the column exists
                  whenever the table does, because it carries the thumbnail as
                  well as the title. "Part" names the option's identity, which
                  is what the cell shows in every data shape. */}
              <th scope="col" className="staging-sourcing-col-part">
                Part
              </th>
              <th scope="col" className="staging-sourcing-col-vendor">
                Vendor / SKU
              </th>
              {/* ONE match column (2026-09-09) — was a `Match %` header plus a
                  separate `Match quality` one. The design has a single 56px
                  match cell holding a word; see `showMatch`'s comment. The
                  surviving name is the semantic one, and it is still a real
                  `columnheader` in the a11y tree (ADR-0146 D8.2) even though
                  the header row is visually clipped. */}
              {showMatch && (
                <th scope="col" className="staging-sourcing-col-match">
                  Match quality
                </th>
              )}
              {/* Price and lead time are ONE stacked column, as the design has
                  them (`Fictiv Platform.dc.html:3254` — a single 82px cell,
                  price above, lead time beneath). This is not cosmetic: the
                  design's row rhythm does not fit them side by side. Measured
                  at the design's own 1196px panel width, the columns left of
                  here consume through x=987 and the Select button occupies
                  1090-1160, leaving ~73px — while "$0.72" plus "0–1 business
                  days" side by side need ~123px. Stacking is how the design
                  buys that space back, so the 14px column rhythm is
                  unachievable without it.

                  It also keeps a `columnheader` for both fields rather than
                  dropping one: exactly the pattern this same table already
                  uses for "Vendor / SKU", which likewise stacks two values
                  under one header. ADR-0146 D8.2's rationale (a screen-reader
                  user needs column headers to interpret a price) is therefore
                  preserved, not traded away. */}
              <th scope="col" className="staging-sourcing-col-price">
                Price / Lead time
              </th>
              <th scope="col" className="staging-sourcing-col-select">
                <span className="sr-only">Select</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {shownOptions.map((option) => {
              const quality = displayedMatchQuality(option)
              return (
                <tr
                  key={option.optionId}
                  className={
                    option.selected
                      ? 'staging-sourcing-option-row staging-sourcing-option-row-selected'
                      : 'staging-sourcing-option-row'
                  }
                >
                  {/* ==== DEFECT FIX, 2026-09-09 — THE MISSING OPTION IMAGE ====
                    Reported by the human: the option rows (including the
                    SELECTED one) had no thumbnail, and so did not line up with
                    the keep row above them.

                    CAUSE: this whole `<td>` — thumbnail included — was wrapped
                    in `{showTitle && …}`, i.e. gated on whether any option
                    carries an `optionTitle`. When none does (the BOM demo
                    projection and the legacy flat surface both ship options
                    without titles) the entire Part column was dropped, taking
                    the image with it and starting the row at Vendor/SKU — 62px
                    left of the keep row's text.

                    THE GATE CONFLATED TWO DIFFERENT DATA DEPENDENCIES. The
                    title comes from `option.optionTitle` and may legitimately
                    be absent; the thumbnail comes from `part.thumbnailUrl` —
                    the ROW's own image, which both surfaces always have. The
                    design confirms they are independent: it draws the thumb as
                    a sibling of the description block on BOTH surfaces
                    (`dc.html:3249` 48px BOM, `:3367` 34px flat) and never
                    conditions it on `desc`, while `matchOptionsFor` (`:6272`)
                    sets `img: base` on every option unconditionally.

                    So the cell is now ALWAYS emitted with its thumbnail, and
                    `showTitle` governs only the title TEXT. The original gating
                    rationale (this file's header comment: don't leave the flat
                    surface "two permanently empty cells per row") is preserved
                    where it actually applies — the cell is never empty now, it
                    always carries the image the design asks for. */}
                  <td className="staging-sourcing-col-part">
                    <div className="staging-sourcing-option-part">
                      {/* Item 7 — the ROW's image on every option, exactly as
                        the comp does (`img: base` on all three options,
                        `matchOptionsFor` at `dc.html:6272`). Uses the MATCHED
                        PART's own thumbnail, not a per-option one: UID-575
                        added `SourcingOption.imageRef` (the wire DOES carry a
                        real per-candidate photo as of that fix, contra what
                        this comment used to claim), but wiring per-option
                        thumbnails into this dropdown is a separate FE follow-up
                        — not fabrication-avoidance, just not yet built. `alt=""`
                        + `aria-hidden` because the option's own title/vendor/SKU
                        already name the row — the thumbnail is decorative, and
                        the same treatment `IdentifiedPartRow`/`MatchedPartRow`
                        already use. */}
                      {/* UID-517 follow-up (PR #514 interaction check) — reuse
                        the SAME shared real-thumbnail-or-placeholder
                        component every other surface this ticket touched
                        already uses, instead of a hand-rolled `<img>`/
                        empty-`<span>` pair: the same `.part-thumbnail-
                        placeholder-glyph` decorative glyph on a miss (never
                        a plain empty box), and the `object-fit: contain`
                        fix already carried by the shared `.staging-row-thumb`
                        class this component keeps using verbatim. */}
                      <PartThumbnail className="staging-row-thumb" url={part.thumbnailUrl} />
                      {/* The design's `flex:1;min-width:0` block. Always present
                        (it is what absorbs the row's remaining width), holding
                        whichever of its two lines the data supports. */}
                      <div className="staging-sourcing-option-titles">
                        {showTitle && (
                          <span
                            className="staging-sourcing-option-title"
                            title={option.optionTitle ?? undefined}
                          >
                            {option.optionTitle}
                          </span>
                        )}
                        {/* The design's confirmation line under a SELECTED
                          option (`Fictiv Platform.dc.html:3251` — the
                          `o.sel` branch). It is NOT redundant with the
                          chip: the chip is at the far right of a ~1100px
                          row, so on a wide viewport the eye has no
                          confirmation next to the part it just chose. It
                          also means the selected state is carried by text
                          in two places rather than by the row tint alone
                          (WCAG 1.4.1).
                          NOW REACHABLE WITHOUT A TITLE — it used to be nested
                          inside the `showTitle` gate too, so a title-less
                          selected option (exactly the BOM demo projection's
                          shape) lost its only textual selected-state carrier
                          and left the teal tint doing that job alone, which is
                          the 1.4.1 failure this line exists to prevent. */}
                        {option.selected && (
                          <span className="staging-sourcing-option-matched">
                            Matched to your part
                          </span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="staging-sourcing-col-vendor">
                    <div className="parts-row-name">
                      <span>{option.vendor}</span>
                      <span className="caption-text">{option.sku}</span>
                    </div>
                  </td>
                  {/* THE DESIGN'S SINGLE MATCH CELL (`dc.html:3253`/`:9421`) —
                    replaces the former `Match %` + `Match quality` pair. Shows
                    the WORD, coloured, with the check-circle glyph beside it;
                    see `displayedMatchQuality` for why the percent-to-word map
                    is a rename rather than a derivation. */}
                  {showMatch && (
                    <td className="staging-sourcing-col-match">
                      {quality === null ? (
                        /* ADR-0146 D2/D8.6 — an option with no quality signal
                         carries NOTHING here; the Catalog service exposes no
                         similarity grade and fabricating one is the exact harm
                         D2 forbids. The visual glyph alone fails 1.1.1/4.1.2 (a
                         bare "—" reads as nothing to a screen reader), so it
                         always pairs with an `sr-only` equivalent — never a bare
                         dash, and never a guessed "Similar". */
                        <>
                          <span aria-hidden="true">—</span>
                          <span className="sr-only">Match quality not available</span>
                        </>
                      ) : (
                        /* The badge WORD renders inside the span, so colour is
                         never the only carrier of this distinction
                         (WCAG 1.4.1) — the three hues are reinforcement, not
                         the signal.
                         THE ICON IS COLOURED BY INHERITANCE, which is the other
                         half of the reported defect: `.staging-sourcing-quality-
                         {exact,similar,partial}` set `color`, and the glyph
                         strokes in `currentColor` — exactly the design's own
                         mechanism (`stroke="currentColor"` under
                         `{{ o.matchStyle }}`). In the deleted percent branch the
                         same icon sat OUTSIDE any tone class, so it rendered in
                         flat body grey next to "100%". */
                        <span
                          className={`staging-sourcing-quality staging-sourcing-quality-${quality}`}
                        >
                          <CheckCircleOutlined aria-hidden="true" />
                          {MATCH_QUALITY_LABEL[quality]}
                        </span>
                      )}
                    </td>
                  )}
                  {/* ONE stacked cell: price above, lead time beneath — see the
                    `Price / Lead time` header's comment for the measured
                    reason this is not two cells. */}
                  <td className="staging-sourcing-col-price">
                    {option.price ? (
                      /* 2026-09-08 design pass — the design renders the unit
                       price as the row's emphasized number (12px/700 in
                       `--info`); it was inheriting flat body text. Wrapper
                       span only — `formatPrice`'s output is unchanged. */
                      <span className="staging-sourcing-price">{formatPrice(option.price)}</span>
                    ) : (
                      <>
                        <span aria-hidden="true">—</span>
                        <span className="sr-only">Price not available</span>
                      </>
                    )}
                    {/* Item 3 — `leadTimeDisplay` (T4) when the producer sends
                      it, else the previous numeric render. The text form is
                      not derivable from the integer: the comp's own values are
                      `0–1 business days` and `1 week`, and `0d`/`7d` is both
                      wrong and, for the 0 case, actively misleading.
                      `leadTimeDays` is kept and still consulted, per the
                      ticket's "keep the numeric field for any consumer that
                      still wants it".

                      The absent case gets the same `sr-only` treatment as the
                      price and match cells above rather than a bare `—`: a
                      lone em-dash reads as nothing to a screen reader, which
                      is ADR-0146 D8.6's rule. D8.6 names the Match % and Price
                      cells because those were the only dashes at the time;
                      applying it here is the same rule, not a new one. */}
                    {option.leadTimeDisplay != null ? (
                      <span className="staging-sourcing-lead">{option.leadTimeDisplay}</span>
                    ) : option.leadTimeDays !== null ? (
                      <span className="staging-sourcing-lead">{option.leadTimeDays}d</span>
                    ) : (
                      <>
                        <span className="staging-sourcing-lead" aria-hidden="true">
                          —
                        </span>
                        <span className="sr-only">Lead time not available</span>
                      </>
                    )}
                  </td>
                  <td className="staging-sourcing-col-select">
                    <Button
                      variant={option.selected ? 'primary' : 'outline'}
                      size="sm"
                      aria-pressed={option.selected}
                      onClick={() => onSelectOption(option.optionId)}
                    >
                      {/* The design's selected chip carries a check glyph beside
                        the word (`dc.html:3255`); the unselected one is the
                        bare label (`:3256`). `IconCheck` is that exact path.
                        Decorative — the label already names the state, and
                        `aria-pressed` carries it for assistive tech. */}
                      {option.selected ? (
                        <>
                          <IconCheck size={11} />
                          Selected
                        </>
                      ) : (
                        'Select'
                      )}
                    </Button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}
    </div>
  )
}
